import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Spinner, ButtonGroup } from 'react-bootstrap';
import { UserPlus, Pencil, Trash2, Grid, List, Download } from 'lucide-react';
import { showSuccess, showError, showConfirm } from '../../../utils/notifications';
import { User } from '../../../services/types';
import usersService from '../../../services/usersService';
import AllowUsersLoginService from "../../../services/allowUsersLoginService";
import { exportUsersToExcel } from '../../../utils/excelExportUsers';
import DataTable, { ColumnDef, ActionButton, StatusBadge } from '../../../components/common/DataTable';
import { useUserData } from '../../../hooks';
import UsersListView from '../../../components/users/UsersListView';

type ViewMode = 'table' | 'list';

const Users = () => {
    const { canEdit, canDelete, role } = useUserData();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [allowedLogins, setAllowedLogins] = useState<Set<string>>(new Set());
    const [isMobile, setIsMobile] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('table');

    // Detect mobile and force list view
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 920;
            setIsMobile(mobile);
            // Force list view on mobile/tablet
            if (mobile) {
                setViewMode('list');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    type FormData = {
        nome: string;
        num: string;
        email: string;
        permission_id: number;
    };
    const [formData, setFormData] = useState<FormData>({
        nome: '',
        num: '',
        email: '',
        permission_id: 1,
    });
    const [saving, setSaving] = useState(false);

    // Fetch users on component mount
    useEffect(() => {
        fetchUsers();
    }, []);

    /**
     * Fetch all users from API
     */
    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await usersService.getAll();
            console.log('Fetched users:', data);
            setUsers(Array.isArray(data) ? data : []);

            // Check login permissions for each user
            const allowed = new Set<string>();
            if (Array.isArray(data)) {
                for (const user of data) {
                    try {
                        await AllowUsersLoginService.getByEmail(user.email);
                        allowed.add(user.public_id);
                    } catch (err) {
                        // User not allowed to login (404 is expected)
                    }
                }
            }
            setAllowedLogins(allowed);
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to load users';
            console.error('Error loading users:', err);
            setUsers([]); // Set empty array on error

            showError('Error!', errorMessage);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Open modal to add new user
     */
    const handleAdd = () => {
        setEditingUser(null);
        setFormData({ nome: '', num: '', email: '', permission_id: 1 });
        setShowModal(true);
    };

    /**
     * Open modal to edit existing user
     */
    const handleEdit = (user: User) => {
        setEditingUser(user);
        setFormData({
            nome: user.nome,
            num: user.num || '',
            email: user.email,
            permission_id: user.permission_id
        });
        setShowModal(true);
    };

    /**
     * Delete user
     */
    const handleDelete = async (id: string) => {
        const confirmed = await showConfirm(
            'Are you sure?',
            'You won\'t be able to revert this!',
            'Yes, delete it!',
            'Cancel'
        );

        if (!confirmed) {
            return;
        }

        try {
            await usersService.delete(id);
            setUsers(users.filter(user => user.public_id !== id));

            showSuccess('Deleted!', 'User has been deleted successfully.');
        } catch (err: any) {
            showError('Error!', err.response?.data?.error || err.response?.data?.message || 'Failed to delete user');
            console.error('Error deleting user:', err);
        }
    };

    /**
     * Handle login permission change
     */
    const handleAllowLoginChange = async (user: User, isChecked: boolean) => {
        try {
            if (isChecked) {
                // Create login permission
                await AllowUsersLoginService.create({ email: user.email });
                setAllowedLogins(new Set(allowedLogins).add(user.public_id));
                showSuccess('Updated!', 'User can now login.');
            } else {
                // Delete login permission
                await AllowUsersLoginService.delete(user.email);
                const newAllowed = new Set(allowedLogins);
                newAllowed.delete(user.public_id);
                setAllowedLogins(newAllowed);
                showSuccess('Updated!', 'User login permission removed.');
            }
        } catch (err: any) {
            showError('Error!', err.response?.data?.error || err.response?.data?.message || 'Failed to update login permission');
            console.error('Error updating login permission:', err);
        }
    };

    /**
     * Export users to Excel (without password for security)
     */
    const handleDownloadExcel = () => {
        try {
            exportUsersToExcel(users, allowedLogins);
            showSuccess('Success!', 'Users exported to Excel successfully.');
        } catch (err: any) {
            showError('Error!', 'Failed to export users to Excel. Please try again.');
            console.error('Error exporting to Excel:', err);
        }
    };

    /**
     * Validate form inputs
     */
    const validateForm = (): boolean => {
        // Check for empty fields
        if (!formData.nome.trim()) {
            showError('Validation Error', 'Name is required');
            return false;
        }

        if (!formData.email.trim()) {
            showError('Validation Error', 'Email is required');
            return false;
        }

        if (!formData.num.trim()) {
            showError('Validation Error', 'Number is required');
            return false;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            showError('Validation Error', 'Please enter a valid email address');
            return false;
        }

        return true;
    };

    /**
     * Submit form (create or update user)
     */
    const handleSubmit = async () => {
        // Validate form before submitting
        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);

            if (editingUser) {
                // Update existing user
                const updatedUser = await usersService.update(editingUser.public_id, formData);
                setUsers(users.map(user =>
                    user.public_id === editingUser.public_id ? updatedUser : user
                ));

                showSuccess('Updated!', 'User has been updated successfully.');
            } else {
                // Create new user
                const newUser = await usersService.create(formData);
                setUsers([...users, newUser]);

                showSuccess('Created!', 'User has been created successfully.');
            }

            setShowModal(false);
        } catch (err: any) {
            showError('Error!', err.response?.data?.error || err.response?.data?.message || 'Failed to save user');
            console.error('Error saving user:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
            <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-between align-items-center'} mb-4`}>
                {!isMobile && <h4 className="mb-0" style={{ fontSize: '1.5rem' }}>Users Management</h4>}
                <div className={`d-flex gap-2 ${isMobile ? 'w-100' : ''}`}>
                    {/* Super Admin only: Download Excel button */}
                    {role === 'Super Admin' && (
                        <Button
                            variant="outline-success"
                            onClick={handleDownloadExcel}
                            className={isMobile ? '' : ''}
                            title="Export users to Excel"
                        >
                            <Download size={18} className={isMobile ? '' : 'me-2'} />
                            {!isMobile && 'Download Excel'}
                        </Button>
                    )}

                    {/* View Toggle */}
                    <ButtonGroup className={isMobile ? 'flex-grow-1' : ''}>
                        <Button
                            variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                            onClick={() => setViewMode('table')}
                            title={isMobile ? "Table view not available on mobile" : "Table View"}
                            className={isMobile ? 'flex-grow-1' : ''}
                            disabled={isMobile}
                            style={{
                                opacity: isMobile ? 0.6 : 1,
                                cursor: isMobile ? 'not-allowed' : 'pointer',
                                backgroundColor: isMobile ? '#e9ecef' : undefined,
                                borderColor: isMobile ? '#dee2e6' : undefined,
                                color: isMobile ? '#6c757d' : undefined
                            }}
                        >
                            <Grid size={18} />
                        </Button>
                        <Button
                            variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                            onClick={() => setViewMode('list')}
                            title="Card View"
                            className={isMobile ? 'flex-grow-1' : ''}
                        >
                            <List size={18} />
                        </Button>
                    </ButtonGroup>

                    {canEdit && (
                        <Button variant="primary" onClick={handleAdd} className={isMobile ? 'w-100' : ''}>
                            <UserPlus size={18} className="me-2" />
                            {!isMobile && 'Add New User'}
                        </Button>
                    )}
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading users...</p>
                    </Card.Body>
                </Card>
            ) : !Array.isArray(users) || users.length === 0 ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <p className="text-muted">No users found</p>
                        {canEdit && (
                            <Button variant="primary" onClick={handleAdd}>
                                <UserPlus size={18} className="me-2" />
                                Add First User
                            </Button>
                        )}
                    </Card.Body>
                </Card>
            ) : viewMode === 'list' ? (
                <UsersListView
                    users={users}
                    allowedLogins={allowedLogins}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAllowLoginChange={handleAllowLoginChange}
                />
            ) : (
                <>
                    {(() => {
                        // Define table columns
                        const columns: ColumnDef<User>[] = [
                            {
                                key: 'nome',
                                header: 'Name',
                                sortable: true,
                                render: (user) => (
                                    <div style={{ fontWeight: '500', color: '#111827' }}>
                                        {user.nome}
                                    </div>
                                ),
                            },
                            {
                                key: 'email',
                                header: 'Email',
                                sortable: true,
                                render: (user) => (
                                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                        {user.email}
                                    </div>
                                ),
                            },
                            {
                                key: 'num',
                                header: 'Number',
                                sortable: true,
                                render: (user) => (
                                    <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                        {user.num}
                                    </div>
                                ),
                            },
                            {
                                key: 'permission_id',
                                header: 'Role',
                                sortable: true,
                                render: (user) => (
                                    <StatusBadge
                                        status={
                                            user.permission_id === 1
                                                ? "Normal"
                                                : user.permission_id === 2
                                                    ? "Admin"
                                                    : "Super Admin"
                                        }
                                        variant={
                                            user.permission_id === 3
                                                ? "danger"
                                                : user.permission_id === 2
                                                    ? "warning"
                                                    : "secondary"
                                        }
                                    />
                                ),
                            },
                            {
                                key: 'allow_login',
                                header: 'Allow Login?',
                                sortable: false,
                                render: (user) => (
                                    <Form.Check
                                        type="checkbox"
                                        checked={allowedLogins.has(user.public_id)}
                                        disabled={!canEdit}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleAllowLoginChange(user, e.target.checked);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                ),
                            },
                        ];

                        // Define table actions based on permissions
                        const actions: ActionButton<User>[] = [
                            ...(canEdit ? [{
                                label: 'Edit',
                                icon: <Pencil size={14} />,
                                variant: 'secondary' as const,
                                onClick: handleEdit,
                            }] : []),
                            ...(canDelete ? [{
                                label: 'Delete',
                                icon: <Trash2 size={14} />,
                                variant: 'danger' as const,
                                onClick: (user: User) => handleDelete(user.public_id),
                            }] : []),
                        ];

                        return (
                            <DataTable
                                columns={columns}
                                data={users}
                                actions={actions}
                                onRowClick={canEdit ? handleEdit : undefined}
                                emptyMessage="No users found"
                                keyExtractor={(user) => user.public_id}
                            />
                        );
                    })()}
                </>
            )}

            {/* Add/Edit Modal */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                scrollable
                centered={!isMobile}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{editingUser ? 'Edit User' : 'Add New User'}</Modal.Title>
                </Modal.Header>
                <Modal.Body
                    style={{
                        maxHeight: isMobile ? 'calc(100vh - 200px)' : 'calc(100vh - 250px)',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch'
                    }}
                >
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.nome}
                                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                placeholder="Enter name"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="Enter email"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Number <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.num}
                                onChange={(e) => setFormData({ ...formData, num: e.target.value })}
                                placeholder="Enter number"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Select
                                value={formData.permission_id}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        permission_id: Number(e.target.value),
                                    })
                                }
                            >
                                <option value={1}>Normal</option>
                                <option value={2}>Admin</option>
                                <option value={3}>Super-Admin</option>
                            </Form.Select>
                        </Form.Group>

                    </Form>
                </Modal.Body>
                <Modal.Footer
                    style={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 10,
                        backgroundColor: '#fff'
                    }}
                >
                    <Button
                        variant="secondary"
                        onClick={() => setShowModal(false)}
                        disabled={saving}
                        style={{
                            opacity: saving ? 0.6 : 1,
                            cursor: saving ? 'not-allowed' : 'pointer',
                            backgroundColor: saving ? '#e9ecef' : undefined,
                            borderColor: saving ? '#dee2e6' : undefined,
                            color: saving ? '#6c757d' : undefined
                        }}
                    >
                        Cancel
                    </Button>
                    {canEdit && (
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={saving}
                            style={{
                                opacity: saving ? 0.6 : 1,
                                cursor: saving ? 'not-allowed' : 'pointer',
                                backgroundColor: saving ? '#6c757d' : undefined,
                                borderColor: saving ? '#6c757d' : undefined
                            }}
                        >
                            {saving ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Saving...
                                </>
                            ) : (
                                editingUser ? 'Update' : 'Create'
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Users;